// src/components/Sidebar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import {
  FiHome,
  FiUsers,
  FiFileText,
  FiFile,
  FiSettings,
  FiMenu,
  FiPlus,
  FiBriefcase,
  FiBarChart2,
  FiZap,
  FiHelpCircle,
  FiCreditCard, // 💳 Payments
  FiInbox, // ✅ Requests
} from 'react-icons/fi';

import AddWorkModal from './work/AddWorkModal';
import AddClientModal from './AddClientModal';

const STORAGE_KEY = 'sidebar:collapsed';

const BASE_GROUPS = [
  {
    title: 'Core',
    items: [
      { path: '/', label: 'Dashboard', icon: FiHome, exact: true },
      { path: '/clients', label: 'Clients', icon: FiUsers },
      { path: '/quotes', label: 'Quotes', icon: FiFile },
      { path: '/invoices', label: 'Invoices', icon: FiFileText },
      { path: '/payments', label: 'Payments', icon: FiCreditCard },
    ],
  },
  {
    title: 'Operations',
    items: [
      { path: '/requests', label: 'Requests', icon: FiInbox }, // ✅ NEW
      { path: '/work', label: 'Work', icon: FiBriefcase },
    ],
  },
  {
    title: 'Intelligence',
    items: [
      { path: '/analytics', label: 'Analytics', icon: FiBarChart2 },
      { path: '/automation', label: 'Automation', icon: FiZap },
    ],
  },
  {
    title: 'Admin',
    items: [{ path: '/settings', label: 'Settings', icon: FiSettings, adminOnly: true }],
  },
];

function cx(...a) {
  return a.filter(Boolean).join(' ');
}

export default function Sidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Create menu + outside-click
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef(null);

  // Modals
  const [workOpen, setWorkOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);

  // Role for RBAC
  const [role, setRole] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved != null) setCollapsed(saved === '1');

    // Try multiple keys (older/newer parts of the app)
    const tryReadRole = () => {
      const keys = ['profile', 'me', 'user'];
      for (const k of keys) {
        try {
          const obj = JSON.parse(localStorage.getItem(k) || 'null');
          if (obj?.role) return String(obj.role).toLowerCase();
        } catch {
          /* ignore */
        }
      }
      return null;
    };

    const r = tryReadRole();
    if (r) setRole(r);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Ctrl/Cmd+B to toggle
  useEffect(() => {
    const onKey = (e) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (isCmdOrCtrl && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close Create on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) {
        setCreateOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setCreateOpen(false);
  }, [location.pathname]);

  const isDev = useMemo(() => process.env.NODE_ENV !== 'production', []);
  const isActivePath = (base) =>
    location.pathname === base || location.pathname.startsWith(base + '/');

  const groups = useMemo(
    () =>
      BASE_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((i) => !i.adminOnly || role === 'admin'),
      })).filter((g) => g.items.length > 0),
    [role]
  );

  const Brand = (
    <Link
      to="/"
      className="group flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-md"
      title="Home"
      aria-label="Asteriq Home"
    >
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-white grid place-items-center text-sm font-bold shadow-sm">
        A
      </div>
      {!collapsed && (
        <div className="flex flex-col">
          <span className="text-sm font-semibold tracking-wide">Asteriq</span>
          <span className="text-[10px] text-gray-500 -mt-0.5">
            {isDev ? 'Development' : 'Production'}
          </span>
        </div>
      )}
    </Link>
  );

  const CreateMenu = (
    <div className="relative" ref={createRef}>
      <button
        onClick={() => setCreateOpen((v) => !v)}
        className={cx(
          'w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2',
          'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm hover:opacity-95 active:opacity-90',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
        )}
        title={collapsed ? 'Quick Create' : undefined}
        aria-haspopup="menu"
        aria-expanded={createOpen}
      >
        <FiPlus className="text-lg" />
        {!collapsed && <span className="text-sm font-medium">Create</span>}
      </button>

      {createOpen && (
        <div
          role="menu"
          className={cx(
            'absolute z-20 mt-2 w-52 rounded-xl border bg-white shadow-lg',
            collapsed ? 'left-12' : 'left-0'
          )}
        >
          <button
            onClick={() => {
              setCreateOpen(false);
              setWorkOpen(true);
            }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
          >
            <FiBriefcase className="text-gray-500" /> New Work
            <span className="ml-auto text-[10px] text-gray-400">W</span>
          </button>

          <NavLink
            to="/quotes/new"
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() => setCreateOpen(false)}
          >
            <FiFile className="text-gray-500" /> New Quote
            <span className="ml-auto text-[10px] text-gray-400">Q</span>
          </NavLink>

          <NavLink
            to="/invoices/new"
            className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() => setCreateOpen(false)}
          >
            <FiFileText className="text-gray-500" /> New Invoice
            <span className="ml-auto text-[10px] text-gray-400">I</span>
          </NavLink>

          <button
            onClick={() => {
              setCreateOpen(false);
              setClientOpen(true);
            }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
          >
            <FiUsers className="text-gray-500" /> New Client
            <span className="ml-auto text-[10px] text-gray-400">C</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <aside
        className={cx(
          'h-full bg-white border-r',
          'flex flex-col',
          'transition-[width] duration-300 ease-out',
          collapsed ? 'w-[72px]' : 'w-[240px]'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-3 border-b">
          {Brand}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={cx(
              'rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              collapsed ? 'p-1.5 mr-0.5' : 'p-2'
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title="Toggle sidebar (Ctrl/Cmd + B)"
          >
            <FiMenu className="text-gray-700" size={18} />
          </button>
        </div>

        {/* Quick Create */}
        <div className={cx('px-3 pt-3', collapsed && 'px-2')}>{CreateMenu}</div>

        {/* Navigation */}
        <nav className="mt-3 pb-4 flex-1 overflow-y-auto">
          {groups.map((group) => (
            <div key={group.title} className="mb-2">
              {!collapsed && (
                <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-gray-500/80 select-none">
                  {group.title}
                </div>
              )}
              <ul className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = item.exact
                    ? location.pathname === item.path
                    : isActivePath(item.path);

                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.exact}
                        className={cx(
                          'group mx-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition relative',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                          'border-l-2',
                          active
                            ? 'bg-blue-50 text-blue-700 border-blue-600'
                            : 'hover:bg-gray-50 text-gray-700 border-transparent'
                        )}
                        title={collapsed ? item.label : undefined}
                        aria-label={item.label}
                      >
                        <span
                          className={cx(
                            'grid place-items-center text-[18px]',
                            active ? 'text-current' : 'text-gray-600 group-hover:text-gray-800'
                          )}
                          aria-hidden="true"
                        >
                          <Icon />
                        </span>
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t p-3 mt-auto">
          {collapsed ? (
            <div className="flex items-center justify-center text-gray-500">
              <a
                href="mailto:support@asteriq.app"
                className="hover:text-gray-800"
                title="Help & Support"
                aria-label="Help & Support"
              >
                <FiHelpCircle />
              </a>
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs text-gray-600">
              <a
                href="mailto:support@asteriq.app"
                className="inline-flex items-center gap-1 hover:text-gray-900"
                title="Help & Support"
              >
                <FiHelpCircle /> Help
              </a>
              <span className="text-gray-400">{isDev ? 'DEV' : 'PROD'}</span>
            </div>
          )}
        </div>
      </aside>

      {/* New Work modal */}
      {workOpen && (
        <AddWorkModal
          open={workOpen}
          isOpen={workOpen}
          onClose={() => setWorkOpen(false)}
        />
      )}

      {/* New Client modal */}
      {clientOpen && (
        <AddClientModal
          open={clientOpen}
          isOpen={clientOpen}
          users={[]}
          onClose={() => setClientOpen(false)}
          onSuccess={() => setClientOpen(false)}
        />
      )}
    </>
  );
}
